import { Injectable, Injector, ViewContainerRef, inject } from '@angular/core';
import { of, type Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import { ConfirmDialogComponent, type ConfirmDialogContext } from '@lite-workspace/shared/helm-ui/dialog';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { AnalyticsEventName, AnalyticsService } from '@lite-workspace/shared/services/analytics';
import { IdentityStore } from '@lite-workspace/shared/identity';
import type { MyTemplate, PartnerCardPack, TemplateLicenceType } from '../domain/entities/design.models';
import { PartnerShopResource, studioTelegramUrl, type PartnerShopConfig } from '../domain/infrastructure/partner-shop.resource';
import { PartnerShowcaseResource } from '../domain/infrastructure/partner-showcase.resource';
import { PartnerOrderStatusDialogComponent, type PartnerOrderStatusDialogContext } from './partner-order-status-dialog.component';
import {
  PartnerPurchaseIntentDialogComponent,
  type PartnerPurchaseIntentDialogContext,
  type PurchaseIntentResult,
} from './partner-purchase-intent-dialog.component';
import { RentTemplateDialogComponent, type RentTemplateDialogContext } from './rent-template-dialog.component';

/** Where «Перейти к оплате» leads: a rental opens the shared rental product, a purchase this pack's own
 *  product; either one unset falls back to the shop home. */
export function partnerPayUrl(
  config: Pick<PartnerShopConfig, 'shopUrl' | 'rentalUrl'>,
  type: TemplateLicenceType,
  shopProductUrl?: string | null,
): string {
  return (type === 'rental' ? config.rentalUrl : shopProductUrl) || config.shopUrl;
}

/**
 * Buying, renting and «Ждёт оплаты» for a partner template — the same three answers wherever the
 * pack is met: the templates screen, its viewer, and the editor's own panel. The surfaces differ, the
 * rules do not, so they live here once instead of being re-decided per screen.
 */
@Injectable({ providedIn: 'root' })
export class PartnerLicenceService {
  private readonly dialog = inject(BrnDialogService);
  private readonly toast = inject(ToastService);
  private readonly shop = inject(PartnerShopResource);
  private readonly showcase = inject(PartnerShowcaseResource);
  private readonly analytics = inject(AnalyticsService);
  // Resolved at the purchase click only: hosts that merely list packs (editor panel) carry no identity port.
  private readonly injector = inject(Injector);

  /** `counted` — the templates screen's sales funnel; the editor panel's viewer requests uncounted. */
  request(vcr: ViewContainerRef, pack: PartnerCardPack, type: TemplateLicenceType, counted = false): void {
    if (counted) this.analytics.track(AnalyticsEventName.TEMPLATE_LICENCE_CLICKED, { pack_id: pack.id, type });
    if (type === 'rental' && !this.shop.config().rentViaShop) {
      this.openRentRequest(vcr, pack);
      return;
    }

    this.openPurchaseIntent(vcr, pack, type);
  }

  /** «Ждёт оплаты» — the order's own window: where it stands, how to finish paying, and the way
   *  out. Who confirms the payment differs — the studio's shop, or the studio itself over Telegram.
   *  Отдаёт `true`, когда заказ снят и фотограф закрыл уведомление: звавшему пора уходить с экрана. */
  explainWait(vcr: ViewContainerRef, template: MyTemplate): Observable<boolean> {
    const config = this.shop.config();
    const manual = template.type === 'rental' && !config.rentViaShop;
    const ref = this.dialog.open<PartnerOrderStatusDialogContext, boolean>(
      PartnerOrderStatusDialogComponent,
      vcr,
      {
        packName: template.name,
        manual,
        actionUrl: manual
          ? config.studioTg
            ? studioTelegramUrl(config.studioTg)
            : ''
          : partnerPayUrl(config, template.type, template.shopProductUrl),
        // Окно отмены открывается ПОВЕРХ этого — закрыть своё раньше значит унести его с собой: Spartan
        // рушит вью закрытого диалога только после exit-анимации. Уходим, когда заказ уже снят.
        cancelOrder: () => {
          this.cancelPending(vcr, template).subscribe(cancelled => {
            if (cancelled) ref.close(true);
          });
        },
      },
      { hasBackdrop: true },
    );

    return ref.closed$.pipe(
      take(1),
      map(outcome => outcome === true),
    );
  }

  /** The wait is not a trap: while the studio has not confirmed the payment, the request can be taken back —
   *  and a payment that lands anyway still brings the pack in on its own, so nothing is lost by cancelling.
   *  Отдаёт `true` один раз — когда заказ снят и уведомление об этом закрыто. */
  cancelPending(vcr: ViewContainerRef, template: MyTemplate): Observable<boolean> {
    const ref = this.dialog.open<ConfirmDialogContext, boolean>(
      ConfirmDialogComponent,
      vcr,
      {
        title: 'Отменить заказ?',
        message: [
          'Если студии вы ещё не заплатили и передумали — отменим заказ, шаблон уйдёт из «Моих шаблонов».',
          'Если оплата всё же пройдёт, шаблон появится сам.',
        ],
        confirmLabel: 'Отменить заказ',
        cancelLabel: 'Не отменять',
        danger: true,
        onConfirm: () => this.cancel(template),
      },
      { hasBackdrop: true },
    );

    // Подписываемся сами: экран шаблонов за исходом не приходит, а уведомление увидеть обязан и он.
    const cancelled = ref.closed$.pipe(
      take(1),
      switchMap(confirmed => (confirmed === true ? this.noticeCancelled(vcr) : of(false))),
      shareReplay(1),
    );
    cancelled.subscribe();
    return cancelled;
  }

  /** Заказ уже снят — объяснять нечего, поэтому ни строки текста, только «ОК». Окно живёт на том же
   *  ViewContainerRef, что и просмотрщик, так что закрывать его вызвавшему можно лишь после ответа. */
  private noticeCancelled(vcr: ViewContainerRef): Observable<boolean> {
    const ref = this.dialog.open<ConfirmDialogContext, boolean>(
      ConfirmDialogComponent,
      vcr,
      { title: 'Заказ отменён', confirmLabel: 'ОК', cancelLabel: null },
      { hasBackdrop: true },
    );

    // Клик мимо и Esc закрывают окно без ответа — заказ от этого не вернётся, поток идёт дальше.
    return ref.closed$.pipe(
      take(1),
      map(() => true),
    );
  }

  /** The payment leaves our app entirely, so what happens after «Перейти к оплате» is explained before it
   *  happens. Вкладку магазина открывает САМ диалог, прямо в обработчике клика: закрытие Spartan едет
   *  через анимацию, и открытие из продолжения `closed$` уже не считается пользовательским жестом.
   *  Заблокированное окно — не оплата: заявку в этом случае не создаём, а зовём нажать ещё раз. */
  private openPurchaseIntent(vcr: ViewContainerRef, pack: PartnerCardPack, type: TemplateLicenceType): void {
    // No shop address — no way to pay. Filing the request anyway would park the card in «Ждёт оплаты»
    // with no payment to wait for, so the refusal comes before the intent window, not after the confirm.
    const config = this.shop.config();
    const shopUrl = partnerPayUrl(config, type, pack.shopProductUrl);
    if (!shopUrl) {
      this.toast.error('Магазин студии недоступен — попробуйте позже');
      return;
    }

    const ref = this.dialog.open<PartnerPurchaseIntentDialogContext, PurchaseIntentResult>(
      PartnerPurchaseIntentDialogComponent,
      vcr,
      {
        type,
        packName: pack.name,
        priceRub: type === 'purchase' ? pack.purchasePriceRub : pack.rentPriceRub,
        studioName: this.studioName(pack),
        shopUrl,
        email: this.injector.get(IdentityStore).profile()?.email ?? '',
        sharedRental: !!config.rentalUrl,
      },
      { hasBackdrop: true },
    );
    ref.closed$.pipe(take(1)).subscribe(outcome => {
      if (outcome === 'blocked') {
        this.toast.error('Браузер заблокировал окно магазина — разрешите всплывающие окна и нажмите ещё раз');
        return;
      }

      if (outcome !== 'opened') return;
      void this.claim(pack, type);
    });
  }

  private openRentRequest(vcr: ViewContainerRef, pack: PartnerCardPack): void {
    const config = this.shop.config();
    const ref = this.dialog.open<RentTemplateDialogContext, boolean>(
      RentTemplateDialogComponent,
      vcr,
      {
        packName: pack.name,
        priceRub: pack.rentPriceRub,
        studioName: this.studioName(pack),
        studioTg: config.studioTg,
        // Чат открывает сам диалог из обработчика клика — из продолжения `closed$` его съедает блокировщик.
        chatUrl: config.studioTg ? studioTelegramUrl(config.studioTg) : '',
      },
      { hasBackdrop: true },
    );
    ref.closed$.pipe(take(1)).subscribe(confirmed => {
      // The request is created either way — the studio's admin panel is where it is invoiced from.
      if (confirmed === true) void this.claim(pack, 'rental');
    });
  }

  private studioName(pack: PartnerCardPack): string {
    return pack.studio?.name ?? this.showcase.packs()[0]?.studio.name ?? 'Студия';
  }

  private async claim(pack: PartnerCardPack, type: TemplateLicenceType): Promise<void> {
    try {
      await this.shop.requestPending(pack, type);
    } catch {
      this.toast.error('Не удалось оформить заявку — попробуйте ещё раз');
    }
  }

  private async cancel(template: MyTemplate): Promise<void> {
    try {
      await this.shop.cancelPending(template);
    } catch (error) {
      // Проглоченный отказ закрыл бы вопрос как успех — и мы соврали бы «Заказ отменён» по живой заявке.
      this.toast.error('Не удалось отменить заказ — попробуйте ещё раз');
      throw error;
    }
  }
}
